import React from 'react';

export interface CardBlockquoteProps {
  children?: React.ReactNode;
}

type CardType = 'win' | 'warning' | 'quote' | 'note' | 'default';

/**
 * Extracts plain text from React children, handling nested structures.
 */
function extractTextContent(children: React.ReactNode): string {
  if (typeof children === 'string') {
    return children;
  }
  if (typeof children === 'number') {
    return String(children);
  }
  if (Array.isArray(children)) {
    return children.map(extractTextContent).join('');
  }
  if (React.isValidElement(children)) {
    const elementChildren = (children.props as { children?: React.ReactNode }).children;
    if (elementChildren) {
      return extractTextContent(elementChildren);
    }
  }
  return '';
}

/**
 * Detects card type from text content based on emoji prefix and bold label patterns.
 * Matches:
 * - `✅ **Win:**` or `✅ **WIN:**` → 'win'
 * - `⚠️ **Warning:**` or `⚠️ **WARNING:**` → 'warning'
 * - `💬 **Quote:**` or `💬 **QUOTE:**` → 'quote'
 * - `📋 **Note:**` or `📋 **NOTE:**` → 'note'
 * Falls back to 'default' for non-card blockquotes.
 */
function detectCardType(text: string): CardType {
  const trimmed = text.trim();

  // Check for emoji + bold label pattern (case-insensitive on label)
  if (/^✅\s*\*\*win:?\*\*/i.test(trimmed)) {
    return 'win';
  }
  if (/^⚠️\s*\*\*warning:?\*\*/i.test(trimmed)) {
    return 'warning';
  }
  if (/^💬\s*\*\*quote:?\*\*/i.test(trimmed)) {
    return 'quote';
  }
  if (/^📋\s*\*\*note:?\*\*/i.test(trimmed)) {
    return 'note';
  }

  // Also support simple emoji-only prefix for flexibility
  if (/^✅\s+/i.test(trimmed)) {
    return 'win';
  }
  if (/^⚠️\s+/i.test(trimmed)) {
    return 'warning';
  }
  if (/^💬\s+/i.test(trimmed)) {
    return 'quote';
  }
  if (/^📋\s+/i.test(trimmed)) {
    return 'note';
  }

  return 'default';
}

/**
 * CardBlockquote renders blockquotes as either styled cards or standard blockquotes.
 * It detects emoji-prefixed card patterns and applies appropriate styling classes.
 */
export const CardBlockquote: React.FC<CardBlockquoteProps> = ({ children }) => {
  const text = extractTextContent(children);
  const cardType = detectCardType(text);

  if (cardType === 'default') {
    // Standard blockquote rendering
    return <blockquote className="standard-blockquote">{children}</blockquote>;
  }

  // Card rendering with semantic classes
  return (
    <div className={`card-block card-${cardType}`}>
      <div className="card-content">{children}</div>
    </div>
  );
};
